import React, { useState, useEffect } from 'react';
import { Numbers, createPaymentSchedule } from '@buddy-technology/buddy_helpers';
import { useWatch } from 'react-hook-form';
import {
	CardElement, useStripe, useElements, CardNumberElement,
} from '@stripe/react-stripe-js';
import { createPurchasePayload, PAYMENT_OPTIONS } from '@buddy-technology/ion-helpers';
import Button from './UI/Button';
import { purchasePolicy } from '../models/api';
import { PAYMENT_TYPES } from '../models/dictionary';
import { IF, RenderedView } from './helpers';
import { Markdown } from './UI';
import StripeInput from './UI/StripeInput';
import { useEventContext, useDataContext } from '../context';

function Checkout({
	checkoutViewInfo, // holds the view object info
	control,
	Controller,
	errors,
	flatApplication,
	getValues,
	handleSubmit,
	handleSuccess,
	includeCheckout,
	navigate,
	onOptIn,
	register,
	setValue,
	stage,
	variables,
	...viewProps
}) {
	const stripe = useStripe();
	const elements = useElements();
	const { sessionId } = useDataContext();
	const formValues = getValues();

	const contentBelowNav = checkoutViewInfo?.content?.filter(({ position }) => position === 'BELOW_NAV');

	const { nextButtonLabel, backButtonLabel } = checkoutViewInfo || {}; // checkoutViewInfo isn't always present. it's ION-dependent
	const defaultSubmitLabel = includeCheckout ? 'Buy Now' : 'Add to Cart';
	const initialSubmitLabel = nextButtonLabel || defaultSubmitLabel;

	const paymentType = formValues['payment::paymentType'];
	const shouldSkipPayment = paymentType === PAYMENT_TYPES.NO_COLLECT;

	const [premiumTotal, paymentOption, startDate, endDate, timezone] = useWatch({ control, name: ['policy::premiumTotal', 'policy::paymentOption', 'policy::startDate', 'policy::endDate', 'policy::timezone'] });

	const [isLoading, setIsLoading] = useState(false);
	const [checkoutError, setCheckoutError] = useState();
	const [buttonLabel, setButtonLabel] = useState(initialSubmitLabel);

	const eventContext = useEventContext();
	const { eventCallback, viewType } = eventContext;
	useEffect(() => {
		if (viewType === 'paginated') {
			eventCallback('onCheckout', { timestamp: Date.now(), checkoutStatus: 'start', premium: Numbers.toUSD(premiumTotal) });
		}
	}, []);

	const getPurchasePayload = (data) => {
		const payload = createPurchasePayload(data);
		// attach session id
		if (!payload.session) {
			payload.session = {};
		}
		payload.session.sessionId = sessionId;

		payload.partnerId = payload.application.policy.partner;

		return payload;
	};

	const getInvalidFieldsMessage = (error) => {
		// eslint-disable-next-line no-console
		console.log(error);
		let errorMsg = error;
		if (typeof error !== 'string') {
			const errorKeys = Object.keys(error);
			errorMsg = errorKeys.reduce((acc, current, i) => {
				const isLastIndex = i === errorKeys.length - 1;
				const field = flatApplication.find(({ id }) => id === current);
				const fieldLabel = field?.uiDisplay?.label;
				return `${acc} ${fieldLabel || ''}${isLastIndex ? '.' : ','}`;
			}, 'We encountered problems with the following fields: ');
		}
		return errorMsg;
	};

	const showErrorAndResetPayButton = (error) => {
		let errorMsg = error?.message || error?.error || error;
		// TODO: allow arrays
		if (typeof errorMsg !== 'string') {
			errorMsg = 'An unknown error occurred.';
		}
		setIsLoading(false);
		setButtonLabel(initialSubmitLabel);
		setCheckoutError(errorMsg);
		eventCallback('onCheckout', {
			timestamp: Date.now(), checkoutStatus: 'error', checkoutError: errorMsg, premium: Numbers.toUSD(premiumTotal),
		});
	};

	const handleCardError = (error) => {
		if (typeof window !== 'undefined' && window.Sentry) {
			window.Sentry.withScope((scope) => {
				scope.setTags({
					rejectionType: 'card',
				});
				window.Sentry.captureMessage(error?.message || error);
			});
		}
		showErrorAndResetPayButton(error);
	};

	const handleSubmitFormError = (error) => {
		const errorMsg = getInvalidFieldsMessage(error);
		if (typeof window !== 'undefined' && window.Sentry) {
			const messages = Object.values(error).map(({ message }) => message);
			window.Sentry.withScope((scope) => {
				scope.setTags({
					rejectionType: 'submit-form',
				});
				scope.setExtra('rawMessages', JSON.stringify(messages));
				window.Sentry.captureException(errorMsg);
			});
		}
		showErrorAndResetPayButton(errorMsg);
	};

	const handleCheckoutError = (error) => {
		if (typeof window !== 'undefined' && window.Sentry) {
			window.Sentry.withScope((scope) => {
				scope.setTags({
					rejectionType: 'order',
				});
				window.Sentry.captureException(error);
			});
		}
		showErrorAndResetPayButton(error);
	};

	const handleAddToCart = () => {
		setIsLoading(true);
		const payload = getPurchasePayload(formValues);
		onOptIn(payload);
		setIsLoading(false);
	};

	const getPaymentToken = async () => {
		setButtonLabel('Validating Card');
		try {
			const cardElement = elements.getElement(CardElement);
			// mobile version inputs are slightly different, here we grab stripe token using the CardNumberElement
			const cardNumberElement = elements.getElement(CardNumberElement);
			// if cardElement is null (user input their cc info via mobile) let's pass along cardNumberElement instead
			const stripeElement = cardElement || cardNumberElement;
			const res = await stripe.createToken(stripeElement);
			return res;
		} catch (error) {
			return { error };
		}
	};

	const handleCheckout = async () => {
		setIsLoading(true);
		setCheckoutError(null);
		try {
			const payload = getPurchasePayload(getValues());
			if (!shouldSkipPayment) {
				const { token, error } = await getPaymentToken();
				if (error) {
					handleCardError(error);
					return;
				}
				// TODO: handle other payment types (eg: ACH, paymentMethodId).
				// Payment should be guaranteed here, if not, something is wrong with the ion. This will get caught in the catch block and be logged.
				payload.application.payment.paymentToken = token.id;
			}
			setButtonLabel('Processing');

			const createdPolicy = await purchasePolicy(payload, stage);

			const { orderId } = createdPolicy;
			setValue('createdPolicy', createdPolicy); // save the policy return object in case we need to grab data from it in the next screen.

			if (createdPolicy.session) {
				// overwrite session if necessary.
				setValue('session', createdPolicy.session);
			}

			setIsLoading(false);
			handleSuccess(orderId);
			eventCallback('onCheckout', {
				timestamp: Date.now(), checkoutStatus: 'success', premium: Numbers.toUSD(premiumTotal), orderId,
			});
		} catch (error) {
			handleCheckoutError(error);
		}
	};

	const createPolicyWithoutPayment = async (data) => {
		setCheckoutError(null);
		const formState = getValues();
		try {
			const payload = getPurchasePayload({ ...formState, ...data });

			const createdPolicy = await purchasePolicy(payload, stage);

			const { orderId } = createdPolicy;
			handleSuccess(orderId);
			eventCallback('onCheckout', { timestamp: Date.now(), checkoutStatus: 'success', premium: Numbers.toUSD(premiumTotal) });
		} catch (error) {
			handleCheckoutError(error);
		}
	};

	const submitCallBack = includeCheckout ? handleCheckout : handleAddToCart;

	// NOTE: this map is for testing only, this prevents us from having to update the IONs
	const paymentOptionMap = {
		SINGLE_PAYMENT: 'SINGLE',
		QUARTERLY_PAYMENTS: 'QUARTERLY',
	};

	const { totalPayments, paymentAmounts } = createPaymentSchedule({
		startDate,
		endDate,
		paymentOption: paymentOptionMap[paymentOption] || paymentOption,
		premiumTotal,
		timezone,
	});

	const installmentWord = (totalPayments - 1) > 1 ? 'payments' : 'payment';

	// Quick check to see if the first payment is the same as the rest, if not, we render a different message
	const intervalPricingStatement = paymentAmounts[0] === paymentAmounts[1]
		? `After today, you will make ${totalPayments - 1} additional ${installmentWord} of $${paymentAmounts[0]}.`
		: `Your first payment is $${paymentAmounts[0]} with ${totalPayments - 1} equal ${installmentWord} of $${paymentAmounts[1]} following.`;

	const shouldShowPremiumTotal = premiumTotal && !Number.isNaN(parseFloat(premiumTotal));

	const hasCheckoutIframe = !!(checkoutViewInfo?.iframe?.src);

	const shouldShowStripeElement = includeCheckout && !hasCheckoutIframe && !shouldSkipPayment;

	return (
		<div id="checkout-wrapper">
			<IF condition={checkoutViewInfo}>
				<div className="my-4">
					<RenderedView
						{...checkoutViewInfo}
						{...viewProps}
						backButtonLabel={backButtonLabel}
						control={control}
						Controller={Controller}
						errors={errors}
						getValues={getValues}
						onCheckout={createPolicyWithoutPayment}
						navigate={navigate}
						nextButtonLabel={nextButtonLabel}
						register={register}
						stage={stage}
						setValue={setValue}
						variables={variables}
					/>
				</div>
			</IF>
			<IF condition={!checkoutViewInfo}>
				<h2 className="mt-4">Checkout</h2>
			</IF>
			{/* TODO: Show total/monthly/quarterly price */}
			<IF condition={shouldShowStripeElement}>
				<div className="mt-4" id="checkout-stripe-wrapper">
					<IF condition={shouldShowPremiumTotal}>
						<p>
							<span id="premium-total-title" className="font-bold mb-4">Premium Total:</span>
							{' '}
							<span>
								{Numbers.toUSD(premiumTotal)}
							</span>
						</p>
						<IF condition={
							// Show the message below if our payment type is present and not SINGLE, and there are more than 1 total payments.
							Object.keys(PAYMENT_OPTIONS).includes(paymentOption)
							&& totalPayments > 1
							&& (paymentOption !== PAYMENT_OPTIONS.SINGLE)
						}
						>
							<p>
								<span id="today-payment-title" className="font-bold mb-4">Today's Payment:</span>
								{' '}
								<span id="today-payment-amount" className="font-bold mb-4">
									{Numbers.toUSD(paymentAmounts[0])}
								</span>
							</p>
							<p id="interval-pricing-statement">
								{intervalPricingStatement}
							</p>
						</IF>
					</IF>
					<StripeInput />
				</div>
			</IF>
			<IF condition={checkoutError}>
				<p className="input-error">{checkoutError}</p>
			</IF>
			<IF condition={!hasCheckoutIframe}>
				<div id="checkout-button-wrapper">
					<Button
						label={buttonLabel}
						isLoading={isLoading}
						// Log any errors to the console for easier debugging.
						onClick={handleSubmit(submitCallBack, handleSubmitFormError)}
						id="checkout-button"
					/>
					{/* added a back button here, removed it from StepThroughNav so these buttons can line up nicely */}
					<IF condition={viewType !== 'single-form'}>
						<Button
							variant="secondary"
							label={backButtonLabel || 'Back'}
							onClick={() => navigate('BACK')}
							id="checkout-back-button"
						/>
					</IF>

				</div>
			</IF>

			<IF condition={contentBelowNav?.length}>
				{contentBelowNav?.map((md, index) => (
					<Markdown
						getValues={getValues}
						variables={variables}
						// index is guaranteed to be stable here since it comes from static ion.
						// some IONs are using the same markdown in multiple places on the same view, so we need index to identify.
						// eslint-disable-next-line react/no-array-index-key
						key={`${md?.text}-${index}`}
						{...md}
						index={index}
						viewId={checkoutViewInfo.id}
						control={control}
						theme={viewProps.theme}
					/>
				))}
			</IF>
		</div>
	);
}

export default Checkout;
